<template>
  <el-dialog title="生成预收票据" :visible.sync="visible" width="580px" :before-close="handleClose" append-to-body>
    <el-form label-width="100px" size="small">
      <el-form-item label="收费对象">
        <span>{{ form.objName }}</span>
      </el-form-item>
      <el-form-item label="欠费项目">
        <div class="fee-list">
          <el-checkbox :indeterminate="isIndeterminate" v-model="checkAll" @change="handleCheckAll">全选</el-checkbox>
          <el-checkbox-group v-model="selectedFeeIds" @change="handleCheckedChange">
            <div v-for="fee in fees" :key="fee.feeId" class="fee-item">
              <el-checkbox :label="fee.feeId" class="fee-checkbox">
                <span>{{ fee.feeName }}</span>
                <span class="fee-period" v-if="fee.startTime && fee.endTime">
                  ({{ _formatPeriod(fee.startTime, fee.endTime) }})
                </span>
                <span class="fee-amount">¥{{ _fmt(fee.amount) }}</span>
              </el-checkbox>
            </div>
          </el-checkbox-group>
        </div>
      </el-form-item>
      <el-form-item label="应收合计">
        <span class="total-amount">¥{{ totalAmount }}</span>
      </el-form-item>
      <el-form-item label="手写收据编号">
        <el-input v-model="form.receiptCode" placeholder="不填则系统自动生成" clearable maxlength="30" />
      </el-form-item>
      <el-form-item label="备注">
        <el-input v-model="form.remark" placeholder="备注信息（可选）" clearable />
      </el-form-item>
    </el-form>

    <div slot="footer">
      <el-button size="small" @click="handleClose">取消</el-button>
      <el-button type="primary" size="small" :loading="loading" @click="submit">生成预收票据</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { savePreReceipt } from '@/api/fee/preReceiptApi'
import { getCommunityId } from '@/api/community/communityApi'

export default {
  name: 'PreReceiptDialog',
  data() {
    return {
      visible: false,
      loading: false,
      // 提交锁：与 loading 配合，submit 入口处拦截重复触发
      submitting: false,
      communityId: '',
      form: {
        objType: '',
        objId: '',
        objName: '',
        payObjId: '',
        payObjName: '',
        receiptCode: '',
        remark: ''
      },
      fees: [],
      selectedFeeIds: [],
      checkAll: false,
      isIndeterminate: false,
      resolveCallback: null
    }
  },
  computed: {
    totalAmount() {
      return this.selectedFeeIds.reduce((sum, fid) => {
        const fee = this.fees.find(f => f.feeId === fid)
        return sum + (fee ? parseFloat(fee.amount || 0) : 0)
      }, 0).toFixed(2)
    }
  },
  methods: {
    open({ objType, objId, objName, payObjId, payObjName, fees }) {
      this.communityId = getCommunityId()
      // 每次打开重置提交锁，防止上次异常残留导致永久无法提交
      this.submitting = false
      this.loading = false
      this.form.objType = objType || '3333'
      this.form.objId = objId || ''
      this.form.objName = objName || ''
      this.form.payObjId = payObjId || objId || ''
      this.form.payObjName = payObjName || objName || ''
      this.form.receiptCode = ''
      this.form.remark = ''
      this.fees = (fees || []).map(f => ({
        ...f,
        // 预收票据金额优先级：receivableAmount > amountOwed > amount（费用总额）
        amount: parseFloat(f.receivableAmount || f.amountOwed || f.amount || 0).toFixed(2)
      }))
      this.selectedFeeIds = this.fees.map(f => f.feeId)
      this.checkAll = this.fees.length > 0
      this.isIndeterminate = false
      this.visible = true
      return new Promise((resolve) => {
        this.resolveCallback = resolve
      })
    },
    handleCheckAll(val) {
      this.selectedFeeIds = val ? this.fees.map(f => f.feeId) : []
      this.isIndeterminate = false
    },
    handleCheckedChange(value) {
      const count = value.length
      this.checkAll = count === this.fees.length
      this.isIndeterminate = count > 0 && count < this.fees.length
    },
    handleClose() {
      this.visible = false
      if (this.resolveCallback) {
        this.resolveCallback(null)
        this.resolveCallback = null
      }
    },
    async submit() {
      // 提交锁：防止重复点击/双击导致多次调用 savePreReceipt（loading 仅 disable 按钮 UI，
      // 同步事件队列里可能已有多个待执行的 submit，需用 submitting 标志兜底）
      if (this.submitting) {
        return
      }
      if (this.selectedFeeIds.length === 0) {
        this.$message.warning('请选择至少一个欠费项目')
        return
      }
      this.submitting = true
      this.loading = true
      try {
        // 把选中的费用明细（金额、周期）一并传给后端，避免后端再按 feeId 查出全量金额导致翻倍
        const selectedFees = this.selectedFeeIds
          .map(fid => this.fees.find(f => f.feeId === fid))
          .filter(Boolean)
        const feeDetails = selectedFees.map(f => ({
          feeId: f.feeId,
          feeName: f.feeName,
          startTime: f.startTime,
          endTime: f.endTime,
          amount: f.amount
        }))
        console.log('[preReceiptDialog] 生成预收票据 feeDetails:', feeDetails)
        const payload = {
          communityId: this.communityId,
          objType: this.form.objType,
          objId: this.form.objId,
          objName: this.form.objName,
          payObjId: this.form.payObjId,
          payObjName: this.form.payObjName,
          feeIds: this.selectedFeeIds.join(','),
          feeDetails: JSON.stringify(feeDetails),
          receiptCode: this.form.receiptCode || '',
          remark: this.form.remark || ''
        }
        console.log('[preReceiptDialog] 生成预收票据 payload:', payload)
        const res = await savePreReceipt(payload)
        if (res.code === 0) {
          this.$message.success('预收票据已生成')
          this.visible = false
          if (this.resolveCallback) {
            this.resolveCallback(true)
            this.resolveCallback = null
          }
        } else {
          this.$message.error(res.msg || '生成失败')
        }
      } catch (e) {
        this.$message.error('生成预收票据失败')
        console.error(e)
      } finally {
        this.loading = false
        this.submitting = false
      }
    },
    _formatPeriod(start, end) {
      const s = (start || '').substring(0, 10)
      const e = (end || '').substring(0, 10)
      return s && e ? `${s.substring(5)}~${e.substring(5)}` : ''
    },
    _fmt(val) {
      return parseFloat(val || 0).toFixed(2)
    }
  }
}
</script>

<style scoped>
.fee-list {
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  padding: 10px;
}
.fee-item {
  margin: 4px 0;
  padding: 4px 0;
  border-bottom: 1px dashed #EBEEF5;
}
.fee-item:last-child { border-bottom: none; }
.fee-checkbox { width: 100%; }
.fee-period { color: #909399; font-size: 12px; }
.fee-amount { float: right; color: #E6A23C; font-weight: bold; }
.total-amount { font-size: 18px; color: #E6A23C; font-weight: bold; }
</style>
